import { AppSettingsCard } from "@/components/admin/AppSettingsCard";
import { TierLimitsCard } from "@/components/admin/TierLimitsCard";
import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { FileText, FlaskConical, Loader2, Shield } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function AdminPage() {
  const breadcrumbs = useMemo(
    () => [
      { label: "Recipe Boxes", href: "/home" },
      { label: "Admin" },
    ],
    [],
  );
  usePageBreadcrumbs(breadcrumbs);

  const { user, profile, loading } = useAuthProfile();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    } else if (!loading && user && profile && !profile.isAdmin) {
      // Redirect non-admin users
      navigate("/home");
    }
  }, [loading, user, profile, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user || !profile || !profile.isAdmin) {
    return null; // Will redirect
  }

  return (
    <div className="container max-w-6xl mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="font-headline text-3xl font-semibold tracking-tight">Admin Dashboard</h1>
        </div>
        <p className="text-muted-foreground">Manage and control various aspects of the application.</p>
      </div>

      {/* Admin Features */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              User Management
            </CardTitle>
            <CardDescription>
              Manage user profiles, account tiers, and permissions. Grant free pro tiers to beta users.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/admin/users">Manage Users</Link>
            </Button>
          </CardContent>
        </Card>

        <TierLimitsCard />

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Error Logs
            </CardTitle>
            <CardDescription>
              View, search, and manage application error logs from all platforms (web, mobile, functions).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/admin/logs">View Logs</Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5" />
              Test Harness
            </CardTitle>
            <CardDescription>
              Run live tests for invitation emails, push notifications, recipe import, and more.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/admin/test-harness">Open Test Harness</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Application Settings */}
      <div className="mt-6">
        <AppSettingsCard />
      </div>
    </div>
  );
}

